import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import CircularProgress from '@mui/material/CircularProgress'
import * as bpService from 'services/BpService'
import { getApiErrors } from 'utils/errors'
import { osMapStyle, hasInteractiveVectorMap, waitForMapLibre } from 'utils/maplibre'

const SELECTED_BUILDING_LAYER_IDS = [
  'selected-os-buildings-fill',
  'selected-os-buildings-line'
]
const LINKED_IDENTIFIERS_BATCH_SIZE = 20
const AUTO_MATCH_CANDIDATE_LIMIT = 180
const MANUAL_SELECT_TOLERANCE_PX = 18
const BUILDING_FEATURE_HINTS = [
  'building',
  'topographicarea',
  'topographic area'
]
const NON_BUILDING_LAYER_HINTS = [
  'boundary',
  'cartographic',
  'contour',
  'label',
  'name',
  'rail',
  'road',
  'symbol',
  'text',
  'water'
]

const POSTCODE_AREA_ZOOM = 16.3
const AUTO_SELECT_LOG_PREFIX = '[OS map auto-select]'

const logAutoSelect = (event, details = {}) => {
  if (process.env.NODE_ENV !== 'production') {
    window.console.info(`${AUTO_SELECT_LOG_PREFIX} ${event}`, details)
  }
}


const normalizeToid = value => {
  const match = String(value || '').match(/(?:osgb)?\d{13,16}/i)

  if (!match) {
    return null
  }

  const toid = match[0].toLowerCase()
  return toid.startsWith('osgb') ? toid : `osgb${toid}`
}

const extractToid = feature => {
  const properties = feature?.properties || {}

  for (const key of [
    'TOID',
    'toid',
    'topographicAreaToid',
    'topographic_area_toid',
    'topography_layer_toid',
    'fid',
    'id'
  ]) {
    const toid = normalizeToid(properties[key] || feature?.[key])

    if (toid) {
      return toid
    }
  }

  return Object.values(properties).map(normalizeToid).find(Boolean)
}

const featureLabel = feature => {
  const properties = feature?.properties || {}

  return (
    properties.name ||
    properties.description ||
    properties.desc ||
    properties.classification ||
    properties.theme ||
    'Selected building'
  )
}

const featureCollection = features => ({
  type: 'FeatureCollection',
  features: features.filter(feature => feature?.geometry)
})

const collectGeometryCoordinates = geometry => {
  if (!geometry) {
    return []
  }

  if (geometry.type === 'GeometryCollection') {
    return (geometry.geometries || []).flatMap(collectGeometryCoordinates)
  }

  const collect = coordinates => {
    if (!Array.isArray(coordinates)) {
      return []
    }

    if (
      coordinates.length >= 2 &&
      typeof coordinates[0] === 'number' &&
      typeof coordinates[1] === 'number'
    ) {
      return [[coordinates[0], coordinates[1]]]
    }

    return coordinates.flatMap(collect)
  }

  return collect(geometry.coordinates)
}

const collectFeatureCoordinates = features =>
  features.flatMap(item => collectGeometryCoordinates(item?.feature?.geometry))

const centerOfCoordinates = coordinates => {
  const validCoordinates = coordinates.filter(
    coordinate =>
      Array.isArray(coordinate) &&
      Number.isFinite(coordinate[0]) &&
      Number.isFinite(coordinate[1])
  )

  if (!validCoordinates.length) {
    return null
  }

  const totals = validCoordinates.reduce(
    (nextTotals, coordinate) => [
      nextTotals[0] + coordinate[0],
      nextTotals[1] + coordinate[1]
    ],
    [0, 0]
  )

  return [
    totals[0] / validCoordinates.length,
    totals[1] / validCoordinates.length
  ]
}

const fitMapToCoordinates = (
  map,
  coordinates,
  zoom = 17.5,
  { animate = true } = {}
) => {
  const validCoordinates = coordinates.filter(
    coordinate =>
      Array.isArray(coordinate) &&
      Number.isFinite(coordinate[0]) &&
      Number.isFinite(coordinate[1])
  )

  if (!validCoordinates.length) {
    return false
  }

  map.resize?.()

  if (validCoordinates.length === 1) {
    map.flyTo({
      center: validCoordinates[0],
      zoom,
      duration: animate ? undefined : 0,
      essential: true
    })
    return true
  }

  const bounds = validCoordinates.reduce(
    (nextBounds, coordinate) => nextBounds.extend(coordinate),
    new window.maplibregl.LngLatBounds(validCoordinates[0], validCoordinates[0])
  )
  const containerWidth = map.getContainer?.()?.clientWidth || window.innerWidth
  const padding = containerWidth <= 480 ? 24 : containerWidth <= 768 ? 40 : 72

  map.fitBounds(bounds, {
    padding,
    maxZoom: zoom,
    duration: animate ? 900 : 0,
    essential: true
  })

  return true
}

const prepareMapViewport = map =>
  new Promise(resolve => {
    if (!map || typeof window.requestAnimationFrame !== 'function') {
      map?.resize?.()
      resolve()
      return
    }

    window.requestAnimationFrame(() => {
      map.resize?.()
      window.requestAnimationFrame(resolve)
    })
  })

const waitForMapIdle = map =>
  new Promise(resolve => {
    let resolved = false

    const finish = () => {
      if (resolved) {
        return
      }

      resolved = true
      resolve()
    }

    map.once('idle', finish)
    window.setTimeout(finish, 1200)
  })

const uniqueByToid = features => {
  const seen = new Set()

  return features.filter(item => {
    if (!item?.toid || seen.has(item.toid)) {
      return false
    }

    seen.add(item.toid)
    return true
  })
}

const queryBuildingFeaturesNearCoordinates = (map, coordinates) => {
  const queriedFeatures = []
  const canvas = map.getCanvas()
  const diagnostics = {
    coordinatesChecked: Math.min(coordinates.length, 250),
    coordinatesInsideViewport: 0,
    rawRenderedFeatures: 0,
    rawPolygonFeatures: 0,
    samplePolygonFeatures: []
  }

  coordinates.slice(0, 250).forEach(coordinate => {
    const point = map.project(coordinate)
    const isInsideViewport =
      point.x >= 0 &&
      point.y >= 0 &&
      point.x <= canvas.clientWidth &&
      point.y <= canvas.clientHeight

    if (isInsideViewport) {
      diagnostics.coordinatesInsideViewport += 1
    }

    const clickBox = [
      [point.x - 18, point.y - 18],
      [point.x + 18, point.y + 18]
    ]

    const rawFeatures = map.queryRenderedFeatures(clickBox, {})
    diagnostics.rawRenderedFeatures += rawFeatures.length

    rawFeatures.forEach(feature => {
      const geometryType = featureGeometryType(feature)

      if (['polygon', 'multipolygon'].includes(geometryType)) {
        diagnostics.rawPolygonFeatures += 1

        if (diagnostics.samplePolygonFeatures.length < 12) {
          diagnostics.samplePolygonFeatures.push({
            geometryType: feature?.geometry?.type || null,
            featureId: feature?.id ?? null,
            layerId: feature?.layer?.id || null,
            sourceId: feature?.layer?.source || null,
            sourceLayer:
              feature?.layer?.sourceLayer ||
              feature?.layer?.['source-layer'] ||
              feature?.sourceLayer ||
              null,
            propertyKeys: Object.keys(feature?.properties || {}),
            properties: feature?.properties || {}
          })
        }
      }

      if (isSelectableBuildingFeature(feature)) {
        const toid = extractToid(feature)

        queriedFeatures.push({
          toid,
          label: featureLabel(feature),
          feature: {
            type: 'Feature',
            geometry: feature.geometry,
            properties: {
              ...(feature.properties || {}),
              toid
            }
          }
        })
      }
    })
  })

  logAutoSelect('coordinate scan raw features', diagnostics)

  return uniqueByToid(queriedFeatures)
}

const queryBuildingFeaturesInViewport = map => {
  const canvas = map.getCanvas()

  return uniqueByToid(
    map
      .queryRenderedFeatures(
        [
          [0, 0],
          [canvas.clientWidth, canvas.clientHeight]
        ],
        {}
      )
      .filter(isSelectableBuildingFeature)
      .map(feature => {
        const toid = extractToid(feature)

        return {
          toid,
          label: featureLabel(feature),
          feature: {
            type: 'Feature',
            geometry: feature.geometry,
            properties: {
              ...(feature.properties || {}),
              toid
            }
          }
        }
      })
  )
}

const queryBuildingFeaturesInSources = (map, targetToids = []) => {
  const style = map.getStyle?.()
  const layers = style?.layers || []
  const targetToidSet = new Set(
    (targetToids || []).map(normalizeToid).filter(Boolean)
  )
  const queriedFeatures = []
  const queriedSourceLayers = new Set()
  const diagnostics = {
    queriedSourceLayers: 0,
    sourceErrors: 0,
    rawFeatures: 0,
    rawFeaturesWithToid: 0,
    targetFeatures: [],
    samplePolygonFeatures: []
  }

  layers.forEach(layer => {
    const sourceId = layer?.source
    const sourceLayer = layer?.['source-layer']

    if (!sourceId || !sourceLayer) {
      return
    }

    const sourceLayerKey = `${sourceId}:${sourceLayer}`

    if (queriedSourceLayers.has(sourceLayerKey)) {
      return
    }

    queriedSourceLayers.add(sourceLayerKey)
    diagnostics.queriedSourceLayers += 1

    try {
      map.querySourceFeatures(sourceId, { sourceLayer }).forEach(feature => {
        diagnostics.rawFeatures += 1
        const toid = extractToid(feature)

        if (toid) {
          diagnostics.rawFeaturesWithToid += 1
        }

        const isTarget = Boolean(toid && targetToidSet.has(toid))
        const selectable = isSelectableBuildingFeature(feature)

        if (
          ['polygon', 'multipolygon'].includes(featureGeometryType(feature)) &&
          diagnostics.samplePolygonFeatures.length < 12
        ) {
          diagnostics.samplePolygonFeatures.push({
            geometryType: feature?.geometry?.type || null,
            featureId: feature?.id ?? null,
            sourceId,
            sourceLayer,
            propertyKeys: Object.keys(feature?.properties || {}),
            properties: feature?.properties || {}
          })
        }

        if (isTarget && diagnostics.targetFeatures.length < 20) {
          diagnostics.targetFeatures.push({
            toid,
            selectable,
            geometryType: feature?.geometry?.type || null,
            featureText: featureText(feature).slice(0, 240),
            sourceId,
            sourceLayer
          })
        }

        if (!selectable) {
          return
        }

        if (targetToidSet.size && !isTarget) {
          return
        }

        queriedFeatures.push({
          toid,
          label: featureLabel(feature),
          feature: {
            type: 'Feature',
            geometry: feature.geometry,
            properties: {
              ...(feature.properties || {}),
              toid
            }
          }
        })
      })
    } catch (error) {
      diagnostics.sourceErrors += 1
      // Some sources are not queryable while tiles are still settling.
    }
  })

  if (targetToidSet.size) {
    logAutoSelect('source scan', {
      targetToids: Array.from(targetToidSet),
      ...diagnostics,
      selectableTargetToids: uniqueByToid(queriedFeatures).map(
        item => item.toid
      )
    })
  }

  return uniqueByToid(queriedFeatures)
}

const sameGeometry = (left, right) => {
  if (!left || !right) {
    return false
  }

  return JSON.stringify(left) === JSON.stringify(right)
}

const isSelectedBuildingLayer = feature =>
  SELECTED_BUILDING_LAYER_IDS.includes(feature?.layer?.id)

const featureGeometryType = feature =>
  String(feature?.geometry?.type || '').toLowerCase()

const featureText = feature => {
  const properties = feature?.properties || {}

  return [
    feature?.layer?.id,
    feature?.layer?.source,
    feature?.layer?.sourceLayer,
    feature?.layer?.['source-layer'],
    feature?.sourceLayer,
    properties.theme,
    properties.descriptiveGroup,
    properties.descriptiveTerm,
    properties.description,
    properties.classification,
    properties.featureType,
    properties.feature_type,
    properties.sourceLayer
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const isSelectableBuildingFeature = feature => {
  if (isSelectedBuildingLayer(feature) || !extractToid(feature)) {
    return false
  }

  if (!['polygon', 'multipolygon'].includes(featureGeometryType(feature))) {
    return false
  }

  const text = featureText(feature)
  const hasBuildingHint = BUILDING_FEATURE_HINTS.some(hint =>
    text.includes(hint)
  )

  if (hasBuildingHint) {
    return true
  }

  return !NON_BUILDING_LAYER_HINTS.some(hint => text.includes(hint))
}

const chunkArray = (items, size) => {
  const chunks = []

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }

  return chunks
}

const resolveLinkedIdentifiersForToids = async toids => {
  const normalizedToids = Array.from(
    new Set((toids || []).map(normalizeToid).filter(Boolean))
  )

  if (!normalizedToids.length) {
    return {
      data: [],
      uprns: []
    }
  }

  const batches = chunkArray(normalizedToids, LINKED_IDENTIFIERS_BATCH_SIZE)
  logAutoSelect('resolving linked identifiers', {
    toids: normalizedToids,
    batchCount: batches.length,
    batchSizes: batches.map(batch => batch.length)
  })
  const responses = await Promise.all(
    batches.map(batch => bpService.resolveLinkedIdentifiers({ toids: batch }))
  )
  const links = responses.flatMap(response => response?.data?.data || [])
  const uprns = Array.from(
    new Set(responses.flatMap(response => response?.data?.uprns || []))
  )

  const result = {
    data: links,
    uprns
  }

  logAutoSelect('linked identifiers resolved', {
    toids: normalizedToids,
    linksCount: links.length,
    uprnsCount: uprns.length
  })

  return result
}

const OsBuildingMultiSelectMap = ({
  selectedToids = [],
  selectedUprns = [],
  selectedFeatures: externalSelectedFeatures = [],
  focusRequest = null,
  postcodeSearchLoading = false,
  totalLinkedUnitsFound = 0,
  onSelectionChange,
  onUnavailableSelection,
  onFocusSettled,
  disabled = false,
  compact = false,
  hideHeaderTitle = false
}) => {
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const disabledRef = useRef(disabled)
  const onSelectionChangeRef = useRef(onSelectionChange)
  const onUnavailableSelectionRef = useRef(onUnavailableSelection)
  const onFocusSettledRef = useRef(onFocusSettled)
  const selectedFeaturesRef = useRef([])
  const [mapReady, setMapReady] = useState(false)
  const [mapError, setMapError] = useState('')
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupError, setLookupError] = useState('')
  const [focusLoading, setFocusLoading] = useState(false)
  const [focusError, setFocusError] = useState('')
  const [selectedFeatures, setSelectedFeatures] = useState([])

  const selectedSummary = useMemo(
    () => ({
      buildingsCount: selectedFeatures.length || selectedToids.length,
      uprnsCount: selectedUprns.length
    }),
    [selectedFeatures.length, selectedToids.length, selectedUprns.length]
  )
  const hasSelectedBuildings = selectedSummary.buildingsCount > 0
  const initialFocusCoordinates = useMemo(
    () => [
      ...collectFeatureCoordinates(focusRequest?.features || []),
      ...(focusRequest?.coordinates || [])
    ],
    [focusRequest]
  )
  const initialCenter = useMemo(
    () => centerOfCoordinates(initialFocusCoordinates),
    [initialFocusCoordinates]
  )
  const isAutoSelectingPostcode =
    (focusLoading || postcodeSearchLoading) &&
    !hasSelectedBuildings &&
    Boolean(
      postcodeSearchLoading ||
        focusRequest?.targetUprns?.length ||
        focusRequest?.targetToids?.length ||
        focusRequest?.coordinates?.length ||
        focusRequest?.query
    )
  const visibleLinkedUnitsCount = hasSelectedBuildings
    ? selectedSummary.uprnsCount || totalLinkedUnitsFound || 0
    : 0

  const syncHighlight = features => {
    selectedFeaturesRef.current = features
    const source = mapRef.current?.getSource('selected-os-buildings')

    if (source) {
      source.setData(featureCollection(features.map(item => item.feature)))
    }
  }

  const focusSelectedFeatures = features => {
    if (!mapRef.current) {
      return false
    }

    return fitMapToCoordinates(
      mapRef.current,
      collectFeatureCoordinates(features || []),
      18.5,
      { animate: false }
    )
  }

  const resolveUprns = async nextFeatures => {
    const toids = nextFeatures.map(item => item.toid)

    if (!toids.length) {
      onSelectionChangeRef.current?.({ toids: [], uprns: [], features: [] })
      return
    }

    setLookupLoading(true)
    setLookupError('')

    try {
      const data = await resolveLinkedIdentifiersForToids(toids)

      onSelectionChangeRef.current?.({
        toids,
        uprns: data?.uprns || [],
        features: nextFeatures,
        links: data?.data || []
      })
    } catch (error) {
      setLookupError(
        getApiErrors(error)?.trim() ||
          'Could not resolve UPRNs for the selected building.'
      )
      onSelectionChangeRef.current?.({
        toids,
        uprns: [],
        features: nextFeatures,
        links: []
      })
    } finally {
      setLookupLoading(false)
    }
  }

  const clearSelection = () => {
    setSelectedFeatures([])
    syncHighlight([])
    onSelectionChangeRef.current?.({
      toids: [],
      uprns: [],
      features: [],
      links: []
    })
  }

  const autoSelectByTargetUprns = async (focus, retryContext = {}) => {
    const targetUprns = (focus?.targetUprns || [])
      .map(value => String(value || '').trim())
      .filter(Boolean)
    const targetToids = (focus?.targetToids || [])
      .map(normalizeToid)
      .filter(Boolean)
    const coordinates = focus?.coordinates || []

    if ((!targetUprns.length && !targetToids.length) || !mapRef.current) {
      logAutoSelect('skipped', {
        reason: !mapRef.current ? 'map-not-ready' : 'no-target-identifiers',
        ...retryContext
      })
      return false
    }

    await waitForMapIdle(mapRef.current)

    logAutoSelect('attempt started', {
      ...retryContext,
      query: focus?.query || '',
      hintSource: focus?.hintSource || '',
      targetToids,
      targetUprnsCount: targetUprns.length,
      coordinatesCount: coordinates.length,
      zoom: mapRef.current?.getZoom?.()
    })

    const coordinateCandidates = coordinates.length
      ? queryBuildingFeaturesNearCoordinates(mapRef.current, coordinates)
      : []
    const shouldScanViewport =
      targetToids.length > 0 || !coordinateCandidates.length
    const viewportCandidates = shouldScanViewport
      ? queryBuildingFeaturesInViewport(mapRef.current)
      : []
    const sourceCandidates = queryBuildingFeaturesInSources(
      mapRef.current,
      targetToids
    )
    const allCandidates = uniqueByToid([
      ...coordinateCandidates,
      ...viewportCandidates,
      ...sourceCandidates
    ])

    const targetUprnSet = new Set(targetUprns)
    const targetToidSet = new Set(targetToids)
    const candidates = targetToids.length
      ? allCandidates.filter(item => targetToidSet.has(item.toid))
      : allCandidates.slice(0, AUTO_MATCH_CANDIDATE_LIMIT)
    const directToidMatches = targetToids.length ? candidates : []

    logAutoSelect('candidate summary', {
      ...retryContext,
      targetToids,
      coordinateCandidateToids: coordinateCandidates.map(item => item.toid),
      viewportCandidateCount: viewportCandidates.length,
      sourceCandidateToids: sourceCandidates.map(item => item.toid),
      allCandidatesCount: allCandidates.length,
      directMatchToids: directToidMatches.map(item => item.toid),
      missingTargetToids: targetToids.filter(
        toid => !directToidMatches.some(item => item.toid === toid)
      )
    })

    if (!candidates.length) {
      return false
    }

    const hasAllTargetToids =
      targetToidSet.size > 0 && directToidMatches.length === targetToidSet.size

    if (hasAllTargetToids) {
      // The TOIDs came from the postcode hints, so the building selection is
      // authoritative. Highlight it before resolving its linked identifiers
      // and never send neighbouring map polygons to the API.
      setSelectedFeatures(directToidMatches)
      syncHighlight(directToidMatches)
      focusSelectedFeatures(directToidMatches)
      logAutoSelect('buildings highlighted', {
        ...retryContext,
        selectedToids: directToidMatches.map(item => item.toid)
      })

      let data = { data: [], uprns: [] }

      try {
        data = await resolveLinkedIdentifiersForToids(
          directToidMatches.map(item => item.toid)
        )
      } catch (error) {
        logAutoSelect('linked identifiers failed after selection', {
          ...retryContext,
          selectedToids: directToidMatches.map(item => item.toid),
          status: error?.response?.status || null,
          message: getApiErrors(error)?.trim() || error?.message || ''
        })
        setLookupError(
          getApiErrors(error)?.trim() ||
            'The building was selected, but its linked UPRNs could not be resolved.'
        )
      }

      const linkedUprns = Array.from(new Set(data?.uprns || []))
      const matchedTargetUprns = linkedUprns.filter(uprn =>
        targetUprnSet.has(String(uprn))
      )
      const otherLinkedUprns = linkedUprns.filter(
        uprn => !targetUprnSet.has(String(uprn))
      )
      const resolvedUprns = [...matchedTargetUprns, ...otherLinkedUprns]
      const nextUprns = resolvedUprns.length ? resolvedUprns : targetUprns

      onSelectionChangeRef.current?.({
        toids: directToidMatches.map(item => item.toid),
        uprns: nextUprns,
        features: directToidMatches,
        links: data?.data || [],
        source: 'postcode-autoselect',
        hintSource: focus?.hintSource || 'local',
        referenceDetails: focus?.referenceDetails || null,
        searchedPostcode: focus?.query || ''
      })

      return true
    }

    // When explicit TOIDs exist, wait for their vector tiles instead of
    // resolving every building around the postcode as an UPRN fallback.
    if (targetToids.length) {
      logAutoSelect('waiting for target TOIDs', {
        ...retryContext,
        targetToids,
        foundToids: directToidMatches.map(item => item.toid),
        missingToids: targetToids.filter(
          toid => !directToidMatches.some(item => item.toid === toid)
        )
      })
      return false
    }

    if (!targetUprns.length) {
      return false
    }

    const data = await resolveLinkedIdentifiersForToids(
      candidates.map(item => item.toid)
    )
    const matchingLinks = (data?.data || []).filter(link =>
      (link?.uprns || []).some(uprn => targetUprnSet.has(String(uprn)))
    )
    const matchingToids = new Set(
      matchingLinks.map(link => normalizeToid(link?.toid)).filter(Boolean)
    )

    if (!matchingToids.size) {
      const canUseCoordinateFallback =
        coordinateCandidates.length &&
        (['local', 'local-uprn-postcodes'].includes(focus?.hintSource) ||
          (focus?.hintSource === 'postcoder-fallback' &&
            coordinateCandidates.length === 1))

      if (canUseCoordinateFallback) {
        const nextFeatures = coordinateCandidates
        const nextUprns = targetUprns

        setSelectedFeatures(nextFeatures)
        syncHighlight(nextFeatures)
        focusSelectedFeatures(nextFeatures)
        onSelectionChangeRef.current?.({
          toids: nextFeatures.map(item => item.toid),
          uprns: nextUprns,
          features: nextFeatures,
          links: [],
          source: 'postcode-coordinate-autoselect',
          hintSource: focus?.hintSource || 'local',
          referenceDetails: focus?.referenceDetails || null,
          searchedPostcode: focus?.query || ''
        })

        return true
      }

      return false
    }

    const nextFeatures = candidates.filter(item => matchingToids.has(item.toid))
    const linkedUprns = Array.from(
      new Set(matchingLinks.flatMap(link => link?.uprns || []))
    )
    const matchedTargetUprns = linkedUprns.filter(uprn =>
      targetUprnSet.has(String(uprn))
    )
    const otherLinkedUprns = linkedUprns.filter(
      uprn => !targetUprnSet.has(String(uprn))
    )
    const nextUprns = [...matchedTargetUprns, ...otherLinkedUprns]

    setSelectedFeatures(nextFeatures)
    syncHighlight(nextFeatures)
    focusSelectedFeatures(nextFeatures)
    onSelectionChangeRef.current?.({
      toids: nextFeatures.map(item => item.toid),
      uprns: nextUprns,
      features: nextFeatures,
      links: matchingLinks,
      source: 'postcode-autoselect',
      hintSource: focus?.hintSource || 'local',
      referenceDetails: focus?.referenceDetails || null,
      searchedPostcode: focus?.query || ''
    })

    return true
  }

  const autoSelectByTargetUprnsWithRetry = async (focus, attempts = 6) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (
        await autoSelectByTargetUprns(focus, {
          attempt: attempt + 1,
          attempts
        })
      ) {
        logAutoSelect('completed', {
          attempt: attempt + 1,
          attempts,
          targetToids: focus?.targetToids || []
        })
        return true
      }

      if (mapRef.current) {
        await waitForMapIdle(mapRef.current)
      }
      await new Promise(resolve => window.setTimeout(resolve, 450))
    }

    // The TOID hint is the fast path, but the exact feature can occasionally
    // be absent from the loaded OS vector tile. Fall back to matching the
    // visible building outlines against the authoritative linked UPRNs.
    if (focus?.targetToids?.length && focus?.targetUprns?.length) {
      logAutoSelect('falling back to UPRN matching', {
        attempts,
        targetToids: focus.targetToids,
        targetUprnsCount: focus.targetUprns.length
      })

      if (
        await autoSelectByTargetUprns(
          { ...focus, targetToids: [] },
          { attempt: 'uprn-fallback', attempts: attempts + 1 }
        )
      ) {
        logAutoSelect('completed with UPRN fallback', {
          targetToids: focus.targetToids,
          targetUprnsCount: focus.targetUprns.length
        })
        return true
      }
    }

    logAutoSelect('all attempts exhausted', {
      attempts,
      targetToids: focus?.targetToids || [],
      targetUprnsCount: focus?.targetUprns?.length || 0
    })
    return false
  }

  useEffect(() => {
    disabledRef.current = disabled
  }, [disabled])

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  useEffect(() => {
    onUnavailableSelectionRef.current = onUnavailableSelection
  }, [onUnavailableSelection])

  useEffect(() => {
    onFocusSettledRef.current = onFocusSettled
  }, [onFocusSettled])

  useEffect(() => {
    let disposed = false

    if (!osMapStyle) {
      return undefined
    }

    waitForMapLibre()
      .then(maplibregl => {
        if (disposed || !mapContainerRef.current || mapRef.current) {
          return
        }

        const map = new maplibregl.Map({
          container: mapContainerRef.current,
          style: osMapStyle,
          center: initialCenter || [-0.1276, 51.5072],
          zoom: initialCenter ? POSTCODE_AREA_ZOOM : 17,
          minZoom: 6,
          attributionControl: false
        })

        mapRef.current = map
        map.addControl(
          new maplibregl.NavigationControl({ showCompass: false }),
          'top-right'
        )
        map.addControl(
          new maplibregl.AttributionControl({ compact: true }),
          'bottom-right'
        )

        map.on('load', () => {
          if (disposed) {
            return
          }

          map.addSource('selected-os-buildings', {
            type: 'geojson',
            data: featureCollection([])
          })
          map.addLayer({
            id: 'selected-os-buildings-fill',
            type: 'fill',
            source: 'selected-os-buildings',
            paint: {
              'fill-color': '#33c3d6',
              'fill-opacity': 0.28
            },
            filter: ['==', ['geometry-type'], 'Polygon']
          })
          map.addLayer({
            id: 'selected-os-buildings-line',
            type: 'line',
            source: 'selected-os-buildings',
            paint: {
              'line-color': '#1292a4',
              'line-width': 3
            },
            filter: ['==', ['geometry-type'], 'Polygon']
          })
          setMapReady(true)
        })

        map.on('click', event => {
          if (disabledRef.current) {
            if (selectedFeaturesRef.current.length) {
              onUnavailableSelectionRef.current?.({
                reason: 'selection-disabled'
              })
            }

            return
          }

          if (!hasInteractiveVectorMap) {
            setLookupError(
              'This OS Maps raster layer is visible, but building multi-select needs OS Vector Tile API enabled for clickable TOIDs.'
            )
            return
          }

          const clickBox = [
            [
              event.point.x - MANUAL_SELECT_TOLERANCE_PX,
              event.point.y - MANUAL_SELECT_TOLERANCE_PX
            ],
            [
              event.point.x + MANUAL_SELECT_TOLERANCE_PX,
              event.point.y + MANUAL_SELECT_TOLERANCE_PX
            ]
          ]
          const currentFeatures = selectedFeaturesRef.current
          const selectedOverlayFeature = map
            .queryRenderedFeatures(clickBox, {
              layers: SELECTED_BUILDING_LAYER_IDS
            })
            .find(feature => extractToid(feature))

          if (selectedOverlayFeature) {
            const selectedOverlayToid = extractToid(selectedOverlayFeature)
            const nextFeatures = currentFeatures.filter(
              item => item.toid !== selectedOverlayToid
            )

            setSelectedFeatures(nextFeatures)
            syncHighlight(nextFeatures)
            resolveUprns(nextFeatures)
            return
          }

          const features = map.queryRenderedFeatures(clickBox, {})
          const buildingFeature = features.find(isSelectableBuildingFeature)

          if (!buildingFeature) {
            setLookupError(
              'No selectable OS building feature found at this point.'
            )

            if (currentFeatures.length) {
              onUnavailableSelectionRef.current?.({
                reason: 'no-selectable-feature'
              })
            }

            return
          }

          const toid = extractToid(buildingFeature)
          const selectedFeature = currentFeatures.find(
            item => item.toid === toid
          )
          if (
            selectedFeature &&
            sameGeometry(
              selectedFeature.feature?.geometry,
              buildingFeature.geometry
            )
          ) {
            const nextFeatures = currentFeatures.filter(
              item => item.toid !== toid
            )

            setSelectedFeatures(nextFeatures)
            syncHighlight(nextFeatures)
            resolveUprns(nextFeatures)
            return
          }

          if (selectedFeature) {
            onUnavailableSelectionRef.current?.({
              reason: 'already-selected-feature'
            })
            return
          }

          const nextFeatures = [
            ...currentFeatures,
            {
              toid,
              label: featureLabel(buildingFeature),
              feature: {
                type: 'Feature',
                geometry: buildingFeature.geometry,
                properties: {
                  ...(buildingFeature.properties || {}),
                  toid
                }
              }
            }
          ]

          setSelectedFeatures(nextFeatures)
          syncHighlight(nextFeatures)
          resolveUprns(nextFeatures)
        })

        map.on('error', event => {
          if (!disposed) {
            setMapError(
              event?.error?.message || 'The OS map could not be loaded.'
            )
          }
        })
      })
      .catch(() => {
        if (!disposed) {
          setMapError('MapLibre could not be loaded.')
        }
      })

    return () => {
      disposed = true
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!selectedToids.length && selectedFeaturesRef.current.length) {
      setSelectedFeatures([])
      syncHighlight([])
    }
  }, [selectedToids.length])

  useEffect(() => {
    if (!mapReady || !mapContainerRef.current || !mapRef.current) {
      return undefined
    }

    let resizeFrame = null
    const resizeMap = () => {
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame)
      }

      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null
        mapRef.current?.resize?.()
      })
    }
    const resizeObserver =
      typeof window.ResizeObserver === 'function'
        ? new window.ResizeObserver(resizeMap)
        : null

    resizeObserver?.observe(mapContainerRef.current)
    window.addEventListener('resize', resizeMap)
    window.addEventListener('orientationchange', resizeMap)
    resizeMap()

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', resizeMap)
      window.removeEventListener('orientationchange', resizeMap)
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame)
      }
    }
  }, [mapReady])

  useEffect(() => {
    if (!mapReady) {
      return
    }

    const nextFeatures = Array.isArray(externalSelectedFeatures)
      ? externalSelectedFeatures.filter(item => item?.feature?.geometry)
      : []

    if (!nextFeatures.length) {
      return
    }

    const hasSameSelection =
      nextFeatures.length === selectedFeaturesRef.current.length &&
      nextFeatures.every(
        (item, index) => item.toid === selectedFeaturesRef.current[index]?.toid
      )

    if (hasSameSelection) {
      return
    }

    setSelectedFeatures(nextFeatures)
    syncHighlight(nextFeatures)
  }, [externalSelectedFeatures, mapReady])

  useEffect(() => {
    const query = String(focusRequest?.query || '').trim()

    if (
      !mapReady ||
      (!query &&
        !focusRequest?.features?.length &&
        !focusRequest?.coordinates?.length) ||
      !mapRef.current
    ) {
      return undefined
    }

    let disposed = false
    const controller = new AbortController()

    const focusMapOnPostcode = async () => {
      setFocusLoading(true)
      setFocusError('')

      try {
        await prepareMapViewport(mapRef.current)

        if (disposed || !mapRef.current) {
          return
        }

        const featureCoordinates = collectFeatureCoordinates(
          focusRequest?.features || selectedFeaturesRef.current
        )

        if (
          fitMapToCoordinates(mapRef.current, featureCoordinates, 18.5, {
            animate: false
          })
        ) {
          return
        }

        if (
          fitMapToCoordinates(
            mapRef.current,
            focusRequest?.coordinates || [],
            18,
            { animate: false }
          )
        ) {
          if (await autoSelectByTargetUprnsWithRetry(focusRequest)) {
            return
          }

          if (
            focusRequest?.targetUprns?.length ||
            focusRequest?.targetToids?.length
          ) {
            setFocusError(
              'We found the postcode area, but could not auto-select the building outline. Please select it on the map.'
            )
          }

          return
        }

        const response = await fetch(
          `https://api.postcodes.io/postcodes/${encodeURIComponent(query)}`,
          { signal: controller.signal }
        )
        const payload = await response.json()
        const longitude = payload?.result?.longitude
        const latitude = payload?.result?.latitude

        if (!response.ok || longitude === null || latitude === null) {
          throw new Error('Postcode not found.')
        }

        if (!disposed) {
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: POSTCODE_AREA_ZOOM,
            duration: 0,
            essential: true
          })

          if (
            focusRequest?.targetUprns?.length ||
            focusRequest?.targetToids?.length
          ) {
            await waitForMapIdle(mapRef.current)
            const autoSelected = await autoSelectByTargetUprnsWithRetry({
              ...focusRequest,
              coordinates: []
            })

            if (!autoSelected) {
              setFocusError(
                'We found the postcode area, but could not auto-select the building outline. Please select it on the map.'
              )
            }
          }
        }
      } catch (error) {
        if (!disposed && error.name !== 'AbortError') {
          setFocusError('We could not find that postcode on the map.')
        }
      } finally {
        if (!disposed) {
          setFocusLoading(false)
          onFocusSettledRef.current?.()
        }
      }
    }

    focusMapOnPostcode()

    return () => {
      disposed = true
      controller.abort()
    }
  }, [focusRequest, mapReady])

  if (!osMapStyle) {
    return (
      <MapShell $compact={compact}>
        <MapUnavailable>
          Set REACT_APP_OS_MAPS_API_KEY or REACT_APP_OS_VECTOR_TILE_API_KEY to
          enable OS map selection.
        </MapUnavailable>
      </MapShell>
    )
  }

  return (
    <MapShell $compact={compact}>
      <MapHeader>
        {!hideHeaderTitle && <MapTitle>Select buildings on OS map</MapTitle>}
        <MapMeta>
          {hasInteractiveVectorMap
            ? `${selectedSummary.buildingsCount} building${
                selectedSummary.buildingsCount === 1 ? '' : 's'
              } selected`
            : 'Raster preview'}
        </MapMeta>
      </MapHeader>

      <MapCanvas ref={mapContainerRef} $compact={compact}>
        {!mapReady && !mapError && (
          <MapLoading>
            <CircularProgress size={16} />
            Loading OS map...
          </MapLoading>
        )}
        {mapError && <MapOverlayMessage>{mapError}</MapOverlayMessage>}
        {isAutoSelectingPostcode && (
          <MapSearchOverlay>
            <CircularProgress size={22} />
            <span>Finding and selecting building outlines...</span>
          </MapSearchOverlay>
        )}
      </MapCanvas>

      <SelectionFooter>
        <SelectionText>
          {lookupLoading ? (
            <>
              <CircularProgress size={13} />
              Resolving UPRNs...
            </>
          ) : focusLoading || postcodeSearchLoading ? (
            <>
              <CircularProgress size={13} />
              Moving map...
            </>
          ) : hasSelectedBuildings ? (
            <>
              {visibleLinkedUnitsCount} total linked unit
              {visibleLinkedUnitsCount === 1 ? '' : 's'} found
            </>
          ) : !focusError &&
            (focusRequest?.targetUprns?.length ||
              focusRequest?.targetToids?.length) ? (
            <>Auto-selecting building outlines from postcode...</>
          ) : (
            <>Select buildings to resolve linked units</>
          )}
        </SelectionText>

        <ClearButton
          type='button'
          onClick={clearSelection}
          disabled={disabled || selectedSummary.buildingsCount === 0}
        >
          Clear map selection
        </ClearButton>
      </SelectionFooter>

      {lookupError && <MapLookupError>{lookupError}</MapLookupError>}
      {focusError && <MapLookupError>{focusError}</MapLookupError>}

      {selectedUprns.length > 0 && (
        <ResolvedUprns title={selectedUprns.join(', ')}>
          Linked UPRNs: {selectedUprns.slice(0, 6).join(', ')}
          {selectedUprns.length > 6 ? ` +${selectedUprns.length - 6} more` : ''}
        </ResolvedUprns>
      )}
    </MapShell>
  )
}

export default OsBuildingMultiSelectMap

const MapShell = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 10px 0 18px;
  border: 1px solid rgba(12, 18, 41, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  ${({ $compact }) =>
    $compact &&
    `
      margin: 0;
    `}
`

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};

  @media (max-width: 480px) {
    align-items: flex-start;
    padding: 8px 10px;
  }
`

const MapTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const MapMeta = styled.div`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
`

const MapCanvas = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  height: clamp(520px, 58vh, 760px);
  min-height: 520px;
  ${({ $compact }) =>
    $compact &&
    `
      height: 220px;
      min-height: 220px;
    `}

  @media (max-width: 768px) {
    height: ${({ $compact }) => ($compact ? '220px' : 'min(52vh, 420px)')};
    height: ${({ $compact }) => ($compact ? '220px' : 'min(52dvh, 420px)')};
    min-height: ${({ $compact }) => ($compact ? '220px' : '300px')};
  }

  @media (max-width: 480px) {
    height: ${({ $compact }) => ($compact ? '210px' : 'min(48vh, 360px)')};
    height: ${({ $compact }) => ($compact ? '210px' : 'min(48dvh, 360px)')};
    min-height: ${({ $compact }) => ($compact ? '210px' : '280px')};
  }

  .maplibregl-ctrl-top-right {
    top: 8px;
    right: 8px;
  }
`

const MapLoading = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f6f8fb;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 13px;
`

const MapOverlayMessage = styled(MapLoading)`
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
`

const MapSearchOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.72);
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  pointer-events: none;
`

const MapUnavailable = styled.div`
  padding: 16px;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 13px;
  line-height: 1.45;
`

const SelectionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.grey[500]};

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
    padding: 8px 10px;
  }
`

const SelectionText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  min-width: 0;
`

const ClearButton = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.grey[600]};
  }
`

const MapLookupError = styled.div`
  padding: 0 12px 10px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 12px;
  line-height: 1.45;
`

const ResolvedUprns = styled.div`
  padding: 0 12px 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 11px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
